import { Injectable, signal, computed } from '@angular/core';
import { Project, Testimonial, JourneyStep, ServiceItem } from '../models/project.model';
import { transformMediaUrl } from '../utils/media-url.util';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private selectedCategorySignal = signal<string>('all');
  private activeLightboxProjectSignal = signal<Project | null>(null);

  readonly selectedCategory = this.selectedCategorySignal.asReadonly();
  readonly activeLightboxProject = this.activeLightboxProjectSignal.asReadonly();

  // Public Showcase Projects Signal initialized with Static Showcase Projects
  projects = signal<Project[]>(this.getInitialDefaultProjects());

  readonly filteredProjects = computed(() => {
    const cat = this.selectedCategorySignal();
    if (cat === 'all') return this.projects();
    return this.projects().filter(p => p.category === cat);
  });

  readonly services = signal<ServiceItem[]>([
    {
      id: 'serv-1',
      title: 'CUSTOM NEW HOMES',
      shortDescription: 'Designed for you. Built for life.',
      fullDescription: 'Create a home that reflects your lifestyle, personality and aspirations. From contemporary family homes to sophisticated architectural residences, we work with you to bring your vision to life.',
      image: '/assets/images/custom_new_homes.png',
      linkText: 'EXPLORE NEW HOMES',
      highlights: ['Bespoke Architectural Plans', 'Site Feasibility & Soil Analysis', 'Fixed Price Contracts', 'Turnkey Interior Handover']
    },
    {
      id: 'serv-2',
      title: 'KNOCK DOWN & REBUILD',
      shortDescription: 'Love your location. Build the home you want.',
      fullDescription: 'A knock-down rebuild gives you the opportunity to stay in the neighbourhood you love while creating a completely new home designed around your needs. We help simplify the journey from existing property to new home.',
      image: '/assets/images/knock_down_and rebuild.png',
      linkText: 'EXPLORE KNOCK DOWN & REBUILD',
      highlights: ['Demolition & Council Management', 'Maximized Block Footprint', 'Modern Passive Thermal Design', 'No Compromise Layouts']
    },
    {
      id: 'serv-3',
      title: 'DUPLEX DESIGNS',
      shortDescription: 'Transform the home you already love.',
      fullDescription: 'Whether you’re extending, renovating or completely transforming your existing home, our team can help turn underused spaces into something exceptional.',
      image: '/assets/images/custom_new_homes.jpg',
      linkText: 'EXPLORE DUPLEX DESIGNS',
      highlights: ['Seamless Old-to-New Integration', 'Structural Engineering & Load Calculations', 'Premium Joinery & Finishes', 'Live-in phased construction capability']
    }
  ]);

  readonly journeySteps = signal<JourneyStep[]>([
    {
      stepNumber: 1,
      title: 'DISCOVER',
      shortDesc: 'Tell us about your project, your block and the home you’ve been imagining.',
      detailDesc: 'We sit down for an in-depth consultation to analyze your site orientation, budget boundaries, family spatial needs, and aesthetic preferences.',
      keyDeliverables: ['Initial Site Analysis', 'Budget Feasibility Check', 'Design Vision Brief'],
      duration: '1-2 Weeks',
      iconName: 'search'
    },
    {
      stepNumber: 2,
      title: 'DESIGN',
      shortDesc: 'Develop a home that balances your lifestyle, aspirations, design preferences and budget.',
      detailDesc: 'Architectural concepts come to life through detailed 3D renders, floorplan iterations, and material selection palettes tailored around your lifestyle.',
      keyDeliverables: ['3D Architectural Renders', 'Concept Floorplans', 'Preliminary Material Schedule'],
      duration: '4-6 Weeks',
      iconName: 'edit'
    },
    {
      stepNumber: 3,
      title: 'PLAN',
      shortDesc: 'Work through selections, documentation, approvals and construction planning.',
      detailDesc: 'We manage all council DA/CDC approval processes, engineering certifications, interior specification finalization, and issue your transparent fixed-price contract.',
      keyDeliverables: ['Council DA/CDC Approval', 'Engineering Certification', 'Fixed-Price Master Contract'],
      duration: '6-10 Weeks',
      iconName: 'file-text'
    },
    {
      stepNumber: 4,
      title: 'BUILD',
      shortDesc: 'Our team brings your plans to life while keeping you informed throughout the construction journey.',
      detailDesc: 'Our experienced directors directly supervise construction on site. You receive weekly progress updates, client portal photos, and key milestone site walks.',
      keyDeliverables: ['Dedicated Director Supervision', 'Weekly Digital Progress Logs', 'Milestone Site Walkthroughs'],
      duration: '8-14 Months',
      iconName: 'hammer'
    },
    {
      stepNumber: 5,
      title: 'HANDOVER',
      shortDesc: 'Walk through your completed home, receive the keys and start living your ambition.',
      detailDesc: 'A meticulous final detail inspection, handover of manuals & warranties, followed by receiving your keys to start living in your dream home.',
      keyDeliverables: ['Comprehensive Quality Inspection', 'Home Warranty Pack', 'Key Handover Ceremony'],
      duration: '1 Week',
      iconName: 'key'
    }
  ]);

  readonly testimonials = signal<Testimonial[]>([
    {
      id: 't-1',
      quote: 'From our first meeting through to handover, the team made the entire process feel personal and well managed. The attention to detail and quality of the finished home exceeded our expectations.',
      author: 'A. & L. Harrison',
      projectType: 'Custom Residence',
      location: 'Sydney, NSW',
      rating: 5,
      image: '/assets/images/interior_living.jpg'
    },
    {
      id: 't-2',
      quote: 'We always felt that our questions were listened to and that the team genuinely cared about the result. We couldn’t be happier with our home.',
      author: 'Marcus & Sophia Chen',
      projectType: 'Knock Down & Rebuild',
      location: 'Strathfield, NSW',
      rating: 5,
      image: '/assets/images/hero_home.png'
    }
  ]);

  public getInitialDefaultProjects(): Project[] {
    return [
      {
        id: 'project-1',
        title: '',
        category: 'duplexDesigns',
        categoryLabel: 'Duplex Residence',
        location: '4 Bronte Pl, Woodbine NSW',
        suburb: 'Woodbine',
        image: '/assets/images/project1/project1.jpg',
        description: 'A masterclass in modern luxury dual-occupancy, featuring two expansive architectural dwellings with high-end finishes.',
        fullOverview: `Commanding an elevated position with impressive street presence, this brand-new duplex delivers a sophisticated take on modern family living, combining generous proportions, a versatile four-bedroom layout and multiple living and entertaining zones across two beautifully considered levels.

A separate lounge creates a welcoming retreat at the front of the home, while the expansive open-plan family and dining domain is anchored by a contemporary kitchen with island bench and adjoining butler's pantry. Designed to transition effortlessly outdoors, this central living space opens to covered alfresco entertaining and a generous rear yard, creating an inviting setting for everyday family life and relaxed gatherings.`,
        buildTime: '10 Months',
        size: '720 sqm',
        completionYear: '2026',
        architect: 'Ambition Built',
        bedrooms: 4,
        bathrooms: 3,
        garageSpaces: 1,
        landArea: '720m²',
        storeyType: 'Double Storey',
        dwellings: [
          { name: 'DWELLING A', bedrooms: 4, bathrooms: 3, livingAreas: 3, garageSpaces: 1 },
          { name: 'DWELLING B', bedrooms: 4, bathrooms: 3, livingAreas: 3, garageSpaces: 1 }
        ],
        features: [
          '<strong>Designer Hampton-Style Kitchen</strong> featuring soft-close cabinetry and drawers for effortless luxury',
          '<strong>Full-height overhead cupboards</strong> crafted to suit 2740 mm ceilings, maximising storage and elegance',
          '<strong>Premium SMEG appliances</strong> delivering superior performance and timeless style',
          '<strong>2340 mm high internal doors</strong> enhancing spatial flow and complementing 2740 mm ceilings',
          '<strong>20 mm porcelain stone benchtops</strong> with a 40 mm waterfall edge for a refined finish',
          '<strong>Custom wardrobe fit-outs</strong> in all bedrooms for seamless organisation',
          '<strong>Frameless shower screens</strong> paired with a <strong>freestanding bath</strong> for a spa-like retreat',
          '<strong>Wall-hung vanities</strong> with contemporary detailing',
          '<strong>Caroma tapware</strong> for a sleek, modern aesthetic',
          '<strong>LED downlights and ceiling fans</strong> throughout for comfort and ambience',
          '<strong>Daikin ducted air conditioning system</strong> ensuring year-round climate control',
          '<strong>Laminated timber flooring</strong> throughout for warmth and sophistication',
          '<strong>Power points with integrated USB and Type-C ports</strong> for convenient, future-ready device charging',
          '<strong>Provision for EV charger point with 32amp</strong> to support sustainable living and future electric vehicle needs'
        ],
        materials: [],
        galleryImages: [
          '/assets/images/project1/1.jpg',
          '/assets/images/project1/3.jpg',
          '/assets/images/project1/4.jpg',
          '/assets/images/project1/5.jpg',
          '/assets/images/project1/6.jpg',
          '/assets/images/project1/7.jpg',
          '/assets/images/project1/8.jpg',
          '/assets/images/project1/9.jpg',
          '/assets/images/project1/11.jpg',
          '/assets/images/project1/12.jpg',
          '/assets/images/project1/13.jpg',
          '/assets/images/project1/14.jpg',
          '/assets/images/project1/15.jpg',
          '/assets/images/project1/16.jpg',

        ],
        videoUrl: '/assets/videos/project1/project1.mp4',
        featured: true
      },
      // {
      //   id: 'mosman-sanctuary',
      //   title: 'The Mosman Sanctuary',
      //   category: 'rebuild',
      //   categoryLabel: 'Knock Down & Rebuild',
      //   location: 'Mosman, Sydney NSW',
      //   suburb: 'Mosman',
      //   image: '/assets/images/luxury_pool.png',
      //   description: 'An elegant coastal knock-down rebuild designed around light, warm natural stone textures, and poolside family entertaining.',
      //   fullOverview: 'Replacing an outdated 1960s bungalow, The Mosman Sanctuary is a modern architectural haven designed for effortless family living. The home features double-height living areas bathed in natural light, surrounded by lush native landscaping and a pristine swimming pool.',
      //   buildTime: '12 Months',
      //   size: '580 sqm',
      //   completionYear: '2023',
      //   architect: 'Ambition Built Architectural Studio',
      //   features: [
      //     'Private Wine Cellar & Tasting Room',
      //     'Double-Height Living Ceiling Void',
      //     'Automated Motorized Timber Louvres',
      //     'Integrated Outdoor BBQ & Kitchen Suite'
      //   ],
      //   materials: ['Natural Travertine Stone', 'Spotted Gum Timber', 'Custom Steel Window Frames', 'Fluted Glass Panels'],
      //   galleryImages: [
      //     '/assets/images/luxury_pool.png',
      //     '/assets/images/interior_living.jpg'
      //   ],
      //   featured: true
      // },
      // {
      //   id: 'vaucluse-duplex-estate',
      //   title: 'The Vaucluse Duplex Estate',
      //   category: 'duplexDesigns',
      //   categoryLabel: 'Duplex Designs',
      //   location: 'Vaucluse, Sydney NSW',
      //   suburb: 'Vaucluse',
      //   image: '/assets/images/duplex.jpg',
      //   description: 'A sophisticated dual-occupancy luxury duplex residence maximizing coastal vistas, natural light, and premium architectural finishes.',
      //   fullOverview: 'The Vaucluse Duplex Estate showcases intelligent spatial planning, creating two expansive luxury dwellings on a single site. Built with premium materials, private landscaped courtyards, and rooftop entertaining spaces.',
      //   buildTime: '16 Months',
      //   size: '850 sqm',
      //   completionYear: '2024',
      //   architect: 'Ambition Built Architectural Studio',
      //   features: [
      //     'Dual Independent Luxury Residences',
      //     'Private Rooftop Entertaining Terrace',
      //     'Acoustically Isolated Party Wall Systems',
      //     'Commercial Grade Elevator Access'
      //   ],
      //   materials: ['Imported Italian Marble', 'Architectural Timber Louvres', 'Off-Form Concrete', 'Low-E Double Glazing'],
      //   galleryImages: [
      //     '/assets/images/duplex.jpg',
      //     '/assets/images/interior_living.jpg',
      //     '/assets/images/hero_home.jpg'
      //   ],
      //   featured: true
      // }
    ];
  }

  fetchProjectsFromBackend() { }
  saveProject(project: Project) { }
  deleteProject(id: string) { }

  setCategory(category: string) {
    this.selectedCategorySignal.set(category);
  }

  getProjectById(id: string): Project | undefined {
    return this.projects().find(p => p.id === id);
  }

  openLightbox(project: Project) {
    this.activeLightboxProjectSignal.set(project);
  }

  closeLightbox() {
    this.activeLightboxProjectSignal.set(null);
  }
}
